import {
  DateInput,
  Select,
  TextAreaInput,
  TextInput,
  Icon,
} from '@octano/global-ui';
import { Fragment, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { useValidations } from '../../../hooks/useValidations';
import { formatOptions } from '../../../utils/selectFormat';
import { icfesFormatOptions } from '../../../utils/selectOptions';
import { useAsyncOptionsFieldsLoader } from '../../../views/StudentFileAsyncOptions/AsyncOptionsFieldsLoader';
import { SectionTitle } from '../../text';

type EmergencyContactReformadaSectionProps = {
  prefix: string;
  optionalFields?: string[];
  fieldNames?: {
    country: string;
    format: string;
    spnNumber: string;
    date: string;
    identificationNumber: string;
    otherExamResults: string;
  };
  disabled?: boolean;
};

/**
 * Para usar este componente tu formulario debe estar envuelto en su FormProvider
 * para poder acceder a la información del formulario a través de useFormContext
 * Los nombres agregados en fieldNames se relacionan a los campos que utilizaste
 * al definir tu formulario.
 */
const DynamicIcfesInfoReformada = ({
  prefix,
  optionalFields = [],
  fieldNames = {
    country: 'icfes_country',
    format: 'icfes_format',
    spnNumber: 'icfes_snpNumber',
    date: 'icfes_date',
    identificationNumber: 'icfes_identificationNumber',
    otherExamResults: 'icfes_otherExamResults',
  },
  disabled = false,
}: EmergencyContactReformadaSectionProps) => {
  const { t } = useTranslation();
  const { control, watch, formState, setValue } = useFormContext();
  const { validateTextNotEmpty } = useValidations();
  const {
    data: { tenanCountry, countries } = {
      data: [],
    },
  } = useAsyncOptionsFieldsLoader();

  const [selectedCountry] = watch([fieldNames.country]);

  const isCountryTouched = formState.touchedFields[fieldNames.country];

  useEffect(() => {
    if (isCountryTouched) {
      setValue(fieldNames.otherExamResults, null);
    }
  }, [
    selectedCountry,
    setValue,
    isCountryTouched,
    fieldNames.otherExamResults,
  ]);

  // Memo
  const isColombia = useMemo(() => {
    return selectedCountry?.value === tenanCountry?.id;
  }, [selectedCountry?.value, tenanCountry]);

  const countryOptions = useMemo(() => {
    return formatOptions(countries ?? []);
  }, [countries]);

  const fields = useMemo(() => {
    return (
      <Fragment>
        <Row className="pt-5">
          <Col xs={12}>
            <SectionTitle text={t(`${prefix}.title`)} />
          </Col>
        </Row>
        <Row>
          <Col className="pb-3" xs={12} lg={isColombia ? 4 : 6}>
            <Select
              name={`${fieldNames.country}`}
              label={t(`${prefix}.${fieldNames.country}`)}
              rules={{ validate: validateTextNotEmpty }}
              options={countryOptions}
              isClearable={false}
              control={control}
              shouldUnregister={true}
            />
          </Col>
          {isColombia && (
            <>
              <Col className="pb-3" xs={12} lg={4}>
                <Select
                  name={`${fieldNames.format}`}
                  label={t(`${prefix}.${fieldNames.format}`)}
                  options={icfesFormatOptions}
                  isClearable={false}
                  control={control}
                  shouldUnregister={true}
                />
              </Col>
              <Col className="pb-3" xs={12} lg={4}>
                <TextInput
                  name={fieldNames.spnNumber}
                  label={t(`${prefix}.${fieldNames.spnNumber}`)}
                  control={control}
                />
              </Col>

              <Col className="pb-3" xs={12} lg={4}>
                <TextInput
                  name={fieldNames.identificationNumber}
                  label={t(`${prefix}.${fieldNames.identificationNumber}`)}
                  control={control}
                />
              </Col>
            </>
          )}

          <Col className="pb-3" xs={12} lg={isColombia ? 4 : 6}>
            <DateInput
              name={fieldNames.date}
              label={t(`${prefix}.${fieldNames.date}`)}
              control={control}
            />
          </Col>

          {!isColombia && (
            <Col className="pb-3" xs={12} lg={12}>
              <TextAreaInput
                name={fieldNames.otherExamResults}
                label={t(`${prefix}.${fieldNames.otherExamResults}`)}
                control={control}
                height={120}
                maxLength={250}
                rules={{
                  maxLength: 250,
                }}
              />
              <p
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                <Icon name="information_input" size="1rem" />
                {t(`${prefix}.${fieldNames.otherExamResults}Tooltip`)}
              </p>
            </Col>
          )}
        </Row>
      </Fragment>
    );
  }, [
    fieldNames.country,
    fieldNames.format,
    fieldNames.spnNumber,
    fieldNames.identificationNumber,
    fieldNames.date,
    fieldNames.otherExamResults,
    t,
    validateTextNotEmpty,
    isColombia,
    countryOptions,
    control,
    prefix,
  ]);

  return <>{fields}</>;
};

export default DynamicIcfesInfoReformada;
